import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { SubmissionError, reset, change } from 'redux-form';
import * as securitiesActions from '../../actions/securitiesSnapshotActions';
import * as reitActions from '../../actions/reitActions';
import * as PeriodActions from '../../actions/periodActions';
import * as commonActions from '../../actions/commonActions';
import EditSecuritiesSnapshotComponent from '../../components/reits/EditSecuritiesSnapshotComponent';
import * as actionHelpers from '../../scripts/actionHelpers';
import * as formHelpers from '../../scripts/formHelpers';
import { withRouter } from '../../common/withRouter';
import { store } from '../../store/configureStore';
import * as actions from '../../actions/actionTypes';
/**
 * EditSecuritiesSnapshotContainer container component
 * @extends {React.Component}
 */
export class EditSecuritiesSnapshotContainer extends React.Component {
  /**
   * Creates a new EditSecuritiesSnapshotContainer
   * @constructor
   * @param {Object} props The component properties
   * @param {Object} context The component context
   */
  constructor(props, context) {
    super(props, context);

    this.state = {
      showForm: false,
      formPurpose: '',
      canEdit: true,
      hideZeroBalanceRows: true,
      selectedSecuritiesSnapshot: {
        allSharesFromIssuerValue: 0,
        class: '',
        issuer: '',
        securityName: '',
        reitOwnedSharesValue: 0,
        percentOfVote: 0,
        reportPeriodID: 0,
        reitOrPropertyName: '',
        clientAccountNumber: '',
        clientAccountCode: '',
        isTenPercentValueTestDisabled: 0,
        hasIncompleteSecuritySnapshot: false,
        clientAccountMappingID: null,
        reitSuiteEntityIDForREIT: null,
        reitSuiteEntityIDForProperty: null,
      },
      isTenPercentValueTestDisabledFlag: false,
      showQuarterlyUploadSecuritiesModal: false,
    };
    this.closeForm = this.closeForm.bind(this);
    this.showForm = this.showForm.bind(this);
    this.submitForm = this.submitForm.bind(this);
    this.handleEditButtonClick = this.handleEditButtonClick.bind(this);
    this.handleDismissErrorMessage = this.handleDismissErrorMessage.bind(this);
    this.handleDisableTenPercentValueTestSelection =
      this.handleDisableTenPercentValueTestSelection.bind(this);
    this.reloadIncompleteSecuritiesSnapshot =
      this.reloadIncompleteSecuritiesSnapshot.bind(this);
    this.handleExportSecuritiesQuarterlyData =
      this.handleExportSecuritiesQuarterlyData.bind(this);
    this.handleUploadSecuritiesQuarterlyData =
      this.handleUploadSecuritiesQuarterlyData.bind(this);
    this.handleHideZeroBalanceRows = this.handleHideZeroBalanceRows.bind(this);
    this.toggleQuarterlyUploadSecuritiesModal =
      this.toggleQuarterlyUploadSecuritiesModal.bind(this);
  }

  /**
   * Invoked immediately after mounting occurs
   * @returns {undefined}
   */
  componentDidMount() {
    this.refresh();
  }

  /**
   * Invoked before a mounted component receives new props.
   * @param {Object} nextProps The properties that the component is receiving
   */
  componentDidUpdate(prevProps) {
    if (prevProps.period !== this.props.period) {
      this.setCanEdit(this.props.period);
    }
  }

  handleDismissErrorMessage() {
    this.props.commonActions.clearFormErrors('securitiesSnapshot');
  }

  refresh() {
    // Check if state.period is null, if it is populate period
    if (!this.props.period) {
      this.props.periodActions.fetchPeriodById(this.props.periodId);
    } else {
      this.setCanEdit(this.props.period);
    }

    const displayZeroBalanceSystemGeneratedRows =
      !this.state.hideZeroBalanceRows;
    return this.props.securitiesActions.fetchSecuritiesSnapshotByPeriod(
      this.props.periodId,
      displayZeroBalanceSystemGeneratedRows
    );
  }

  submitForm(values) {
    const threshold = 1e-8;
    if (!values.class) {
      throw new SubmissionError({ name: 'Class is required' });
    } else {
      this.props.commonActions.beginTask();
      const securitiesSnapshot = {
        securitiesSnapshotID: values.securitiesSnapshotID,
        allSharesFromIssuerValue:
          this.state.isTenPercentValueTestDisabledFlag === true
            ? '0.00'
            : values.allSharesFromIssuerValue,
        class: values.class,
        issuer: values.issuer,
        securityName: values.securityName,
        reitOwnedSharesValue:
          values.reitOwnedSharesValue < threshold
            ? '0.00'
            : values.reitOwnedSharesValue,
        percentOfVote: values.percentOfVote,
        reportPeriodID: this.props.reportPeriodID,
        reitOrPropertyName: values.reitOrPropertyName,
        clientAccountNumber: values.clientAccountCode,
        isTenPercentValueTestDisabled:
          this.state.isTenPercentValueTestDisabledFlag,
        isSystemGenerated: values.isSystemGenerated,
        reitSuiteEntityIDForREIT: values.reitSuiteEntityIDForREIT,
        reitSuiteEntityIDForProperty: values.reitSuiteEntityIDForProperty,
      };

      return securitiesActions
        .updateSecuritiesSnapshot(securitiesSnapshot)
        .then((data) => {
          if (actionHelpers.isErrorResponse(data)) {
            this.props.commonActions.endTask();
            formHelpers.generateSubmissionError(data);
            return;
          }

          this.closeForm();
          this.props.commonActions.endTask();
          this.refresh();
          // reload incomplete securities snapshot details so the tab buttons of tab container(e.g. Report button) are enabled/disabled accordingly.
          this.reloadIncompleteSecuritiesSnapshot();
        })
        .catch((error) => {
          if (error instanceof SubmissionError) {
            throw error;
          }

          this.props.commonActions.endTask();
          formHelpers.generateSubmissionError();
        });
    }
  }

  showForm() {
    this.setState({
      showForm: true,
      formPurpose: '',
      selectedSecuritiesSnapshot: null,
    });
  }

  closeForm() {
    this.setState(
      { showForm: false, isTenPercentValueTestDisabledFlag: false },
      () => store.dispatch(reset('securitiesSnapshot'))
    );
  }

  handleEditButtonClick(value) {
    this.setState({
      showForm: true,
      formPurpose: 'EDIT',
      isTenPercentValueTestDisabledFlag: value.isTenPercentValueTestDisabled,
      selectedSecuritiesSnapshot: {
        securitiesSnapshotID: value.securitiesSnapshotID,
        allSharesFromIssuerValue: value.allSharesFromIssuerValue
          ? value.allSharesFromIssuerValue
          : '0',
        class: value.class,
        issuer: value.issuer,
        securityName: value.securityName,
        // For manual entry, ClientAccountMappingID value is null
        // For system created from TB upload, ClientAccountMappingID value is not null. Hence, if reitOwnedSharesValue is less than 0, display 0
        reitOwnedSharesValue: value.reitOwnedSharesValue
          ? value.clientAccountMappingID == null
            ? value.reitOwnedSharesValue
            : value.reitOwnedSharesValue > 0
              ? value.reitOwnedSharesValue
              : '0'
          : '0',
        percentOfVote: value.percentOfVote ? value.percentOfVote : '0',
        reportPeriodID: value.reportPeriodID,
        reitOrPropertyName: value.reitOrPropertyName,
        clientAccountNumber: value.clientAccountCode,
        isTenPercentValueTestDisabled: value.isTenPercentValueTestDisabled,
        hasIncompleteSecuritySnapshot: value.hasIncompleteSecuritySnapshot,
        clientAccountMappingID: value.clientAccountMappingID,
        isSystemGenerated: value.isSystemGenerated,
        reitSuiteEntityIDForREIT: value.reitSuiteEntityIDForREIT,
        reitSuiteEntityIDForProperty: value.reitSuiteEntityIDForProperty,
      },
    });
  }

  setCanEdit(period) {
    const isEnabled = PeriodActions.isTestingEnabled(period);
    this.setState({ canEdit: isEnabled });
  }

  handleDisableTenPercentValueTestSelection(value) {
    this.setState({ isTenPercentValueTestDisabledFlag: value }, () =>
      store.dispatch(
        change('securitiesSnapshot', 'allSharesFromIssuerValue', '')
      )
    );
  }

  /**
   * Reload incomplete securities snapshot details so the tab buttons of tab container(e.g. Report button) are enabled/disabled accordingly.
   */
  reloadIncompleteSecuritiesSnapshot() {
    securitiesActions
      .getIncompleteSecuritySnapshotDetailsWithoutDispatch(
        this.props.clientId,
        this.props.reitId,
        this.props.periodId
      )
      .then((secData) => {
        store.dispatch({
          type: actions.RELOAD_INCOMPLETE_SECURITIES_SNAPSHOTS_SUCCESS,
          incompleteSecuritiesSnapshots: secData,
        });
      });
  }
  handleExportSecuritiesQuarterlyData() {
    this.props.securitiesActions.exportQuarterlySecuritiesData(
      this.props.clientId,
      this.props.periodId
    );
  }

  handleUploadSecuritiesQuarterlyData() {
    this.setState({ showQuarterlyUploadSecuritiesModal: true });
  }
  toggleQuarterlyUploadSecuritiesModal(periodID) {
    this.setState({
      showQuarterlyUploadSecuritiesModal:
        !this.state.showQuarterlyUploadSecuritiesModal,
    });
    if (periodID && periodID === this.props.periodId) {
      const displayZeroBalanceSystemGeneratedRows =
        !this.state.hideZeroBalanceRows;
      this.props.securitiesActions.fetchSecuritiesSnapshotByPeriod(
        this.props.periodId,
        displayZeroBalanceSystemGeneratedRows
      );
    }
  }
  handleHideZeroBalanceRows() {
    this.setState(
      { hideZeroBalanceRows: !this.state.hideZeroBalanceRows },
      function () {
        const displayZeroBalanceSystemGeneratedRows =
          !this.state.hideZeroBalanceRows;
        this.props.securitiesActions
          .fetchSecuritiesSnapshotByPeriod(
            this.props.periodId,
            displayZeroBalanceSystemGeneratedRows
          )
          .then((securitiesSnapshots) => {
            return securitiesSnapshots;
          });
      }
    );
  }
  /**
   * Render a React element
   * @returns {Object} A reference to the component
   */
  render() {
    return (
      <div>
        <EditSecuritiesSnapshotComponent
          securitiesSnapshots={this.props.securitiesSnapshots}
          showForm={this.state.showForm}
          handleCloseForm={this.closeForm}
          handleSubmit={this.submitForm}
          formPurpose={this.state.formPurpose}
          selectedSecuritiesSnapshot={this.state.selectedSecuritiesSnapshot}
          handleEditButtonClick={this.handleEditButtonClick}
          canEdit={this.state.canEdit}
          handleDismissErrorMessage={this.handleDismissErrorMessage}
          isTenPercentValueTestDisabledFlag={
            this.state.isTenPercentValueTestDisabledFlag
          }
          handleDisableTenPercentValueTestSelection={
            this.handleDisableTenPercentValueTestSelection
          }
          handleHideZeroBalanceRows={this.handleHideZeroBalanceRows}
          hideZeroBalanceRows={this.state.hideZeroBalanceRows}
          handleExportSecuritiesQuarterlyData={
            this.handleExportSecuritiesQuarterlyData
          }
          handleUploadSecuritiesQuarterlyData={
            this.handleUploadSecuritiesQuarterlyData
          }
          showQuarterlyUploadSecuritiesModal={
            this.state.showQuarterlyUploadSecuritiesModal
          }
          toggleQuarterlyUploadSecuritiesModal={
            this.toggleQuarterlyUploadSecuritiesModal
          }
        />
      </div>
    );
  }
}

EditSecuritiesSnapshotContainer.propTypes = {
  clientId: PropTypes.number.isRequired,
  reitId: PropTypes.number.isRequired,
  period: PropTypes.object.isRequired,
  periodId: PropTypes.number.isRequired,
  periodStatus: PropTypes.string,
  securitiesActions: PropTypes.object.isRequired,
  reitActions: PropTypes.object,
  periodActions: PropTypes.object.isRequired,
  securitiesSnapshots: PropTypes.arrayOf(
    PropTypes.shape({
      allSharesFromIssuerValue: PropTypes.number,
      class: PropTypes.string,
      securitiesSnapshotID: PropTypes.number,
      issuer: PropTypes.string,
      securityName: PropTypes.string,
      reitOwnedSharesValue: PropTypes.number,
      percentOfVote: PropTypes.number,
      reportPeriodID: PropTypes.number,
      isTenPercentValueTestDisabled: PropTypes.bool,
    })
  ),
  commonActions: PropTypes.object.isRequired,
};

EditSecuritiesSnapshotContainer.contextTypes = {
  router: PropTypes.object,
};

/**
 * Maps items from state to properties of the component
 * @param {Object} state The state
 * @param {Object} ownProps The properties of the component
 * @returns {Object} An object containing properties that the component can access
 */
function mapStateToProps(state, ownProps) {
  return {
    clientId: Number.parseInt(ownProps.router.params.clientId),
    reitId: Number.parseInt(ownProps.router.params.reitId),
    periodId: Number.parseInt(ownProps.router.params.periodId),
    securitiesSnapshots: state.securitiesSnapshots,
    period: state.period,
  };
}

/**
 * Binds actions to the dispatcher
 * @param {Object} dispatch The action dispatcher
 * @returns {Object} An object containing properties that the component can access
 */
function mapDispatchToProps(dispatch) {
  return {
    securitiesActions: bindActionCreators(securitiesActions, dispatch),
    periodActions: bindActionCreators(PeriodActions, dispatch),
    commonActions: bindActionCreators(commonActions, dispatch),
    reitActions: bindActionCreators(reitActions, dispatch),
  };
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(EditSecuritiesSnapshotContainer)
);
